import React, { useState, useEffect, useCallback, Component } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Save,
  X,
  Plus,
  Trash2,
  ClipboardList,
  Calendar,
  User,
  UserCircle,
} from 'lucide-react';
import apiService from '../../services/api';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import CreateCustomerModal from '../../components/CreateModals/CreateCustomerModal';
import CreateScheduleModal from '../../components/CreateModals/CreateScheduleModal';
import CreateSectionModal from '../../components/CreateModals/CreateSectionModal';
import CreateProductModal from '../../components/CreateModals/CreateProductModal';

// Error boundary to catch render crashes (e.g. when loading from BOQ)
class CreateBOQErrorBoundary extends Component<{ children: React.ReactNode }, { hasError: boolean; error: Error | null }> {
  state = { hasError: false, error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('CreateBOQPage error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError && this.state.error) {
      return (
        <div className="p-6 max-w-lg mx-auto">
          <Card padding="md" className="border-red-200 bg-red-50">
            <p className="font-semibold text-red-800 mb-2">Something went wrong</p>
            <p className="text-sm text-red-700 mb-3">{this.state.error.message}</p>
            <Button size="sm" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}

interface CategoryItem {
  _id: string;
  name: string;
  parent?: string | { _id: string; name: string } | null;
}

interface ProductRow {
  id: string;
  productId: string;
  productDetails: any;
  primaryCategoryId?: string;
  subCategoryId?: string;
  qty: number | '';
  unit: string;
}

interface SectionData {
  id: string;
  sectionId: string;
  sectionDetails: any;
  products: ProductRow[];
}

interface ScheduleData {
  id: string;
  scheduleId: string;
  scheduleDetails: any;
  sections: SectionData[];
}

const CreateBOQPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Form Data
  const [customerId, setCustomerId] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<any | null>(null);
  const [projectIndex, setProjectIndex] = useState<number>(-1);
  const [selectedProject, setSelectedProject] = useState<{ name: string; address: string } | null>(null);
  const [projectName, setProjectName] = useState('');
  const [projectAddress, setProjectAddress] = useState('');
  const [boqDate, setBoqDate] = useState(new Date().toISOString().split('T')[0]);
  const [boqSchedules, setBOQSchedules] = useState<ScheduleData[]>([]);

  // Dropdown Data
  const [customers, setCustomers] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [sections, setSections] = useState<any[]>([]);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [products, setProducts] = useState<any[]>([]);
  const [units, setUnits] = useState<any[]>([]);
  const [boqsList, setBoqsList] = useState<any[]>([]);
  const [employees, setEmployees] = useState<Array<{ _id: string; name: string; designation?: string }>>([]);
  const [inquiryEmployeeId, setInquiryEmployeeId] = useState('');
  const [productSearchOpen, setProductSearchOpen] = useState<{ scheduleIdx: number; sectionIdx: number; productIdx: number } | null>(null);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [createScheduleModalOpen, setCreateScheduleModalOpen] = useState(false);
  const [createSectionModalOpen, setCreateSectionModalOpen] = useState(false);
  const [createProductModalOpen, setCreateProductModalOpen] = useState(false);

  const fetchAllData = useCallback(async (options?: { silent?: boolean }) => {
    const silent = options?.silent === true;
    try {
      if (!silent) setLoading(true);
      const [customersRes, schedulesRes, sectionsRes, categoriesRes, productsRes, unitsRes, boqsRes, employeesRes] = await Promise.all([
        apiService.getAllCustomers(),
        apiService.getSchedules(),
        apiService.getSections(),
        apiService.getCategories(),
        apiService.getAllQuotationItems(),
        apiService.getUnits(),
        apiService.getAllBOQs(),
        apiService.getEmployees(),
      ]);

      setCustomers(customersRes.data || []);
      setSchedules(schedulesRes.data.schedules || schedulesRes.data || []);
      setSections(sectionsRes.data.sections || sectionsRes.data || []);
      setCategories(categoriesRes?.data?.categories || categoriesRes?.data || []);
      // Products should have unit populated from backend
      const productsData = productsRes.data || [];
      setProducts(productsData);
      // Log first product to verify unit structure (for debugging)
      if (productsData.length > 0 && productsData[0].unit) {
        console.log('Sample product unit structure:', productsData[0].unit);
      }
      setUnits(unitsRes.data.units || unitsRes.data || []);
      const boqsRaw = boqsRes.data || [];
      setBoqsList(
        [...boqsRaw].sort((a: any, b: any) => {
          const dateA = new Date(a.boqDate || a.createdAt || 0).getTime();
          const dateB = new Date(b.boqDate || b.createdAt || 0).getTime();
          return dateB - dateA;
        })
      );
      setEmployees(employeesRes.data.employees || employeesRes.data || []);

      // If in edit mode, fetch and populate existing BOQ data
      if (isEditMode && id) {
        try {
          const boqRes = await apiService.getBOQ(id);
          const boq = boqRes?.data;
          if (!boq || typeof boq !== 'object') {
            toast.error('Invalid BOQ data received');
            return;
          }
          
          setCustomerId(boq.customer?._id || (typeof boq.customer === 'string' ? boq.customer : '') || '');
          setInquiryEmployeeId(boq.inquiryOf?._id || boq.inquiryOf || '');
          setProjectName(boq.projectName || '');
          setProjectAddress(boq.projectAddress || '');
          if (boq.boqDate) {
            try {
              setBoqDate(new Date(boq.boqDate).toISOString().split('T')[0]);
            } catch {
              setBoqDate(new Date().toISOString().split('T')[0]);
            }
          }
          
          if (typeof boq.customer === 'object' && boq.customer) {
            setSelectedCustomer(boq.customer);
          } else {
            setSelectedCustomer(null);
          }
          
          const rawSchedules = Array.isArray(boq.schedules) ? boq.schedules : [];
          const scheduleList = schedulesRes?.data?.schedules || schedulesRes?.data || [];
          const sectionList = sectionsRes?.data?.sections || sectionsRes?.data || [];
          const productList = productsRes?.data || [];
          
          if (rawSchedules.length > 0) {
            const loadedSchedules = rawSchedules.map((scheduleData: any) => {
              const scheduleRef = scheduleData?.schedule;
              const scheduleIdStr = typeof scheduleRef === 'object' && scheduleRef !== null && scheduleRef._id
                ? scheduleRef._id
                : (typeof scheduleRef === 'string' ? scheduleRef : '');
              const rawSections = Array.isArray(scheduleData?.sections) ? scheduleData.sections : [];
              const loadedSections = rawSections.map((sectionData: any) => {
                const sectionRef = sectionData?.section;
                const sectionIdStr = typeof sectionRef === 'object' && sectionRef !== null && sectionRef._id
                  ? sectionRef._id
                  : (typeof sectionRef === 'string' ? sectionRef : '');
                const rawProducts = Array.isArray(sectionData?.products) ? sectionData.products : [];
                const loadedProducts = rawProducts.map((productData: any) => {
                  const productRef = productData?.product;
                  const productIdStr = typeof productRef === 'object' && productRef !== null && productRef._id
                    ? productRef._id
                    : (typeof productRef === 'string' ? productRef : '');
                  const productDetails = productList.find((p: any) => (p._id || p) === productIdStr) || (typeof productRef === 'object' && productRef !== null ? productRef : null);
                  let unitVal = productData?.unit || '';
                  if (productDetails?.unit && !unitVal) {
                    if (typeof productDetails.unit === 'object' && productDetails.unit !== null) {
                      unitVal = productDetails.unit.symbol || productDetails.unit.name || '';
                    } else {
                      unitVal = String(productDetails.unit);
                    }
                  }
                  const parentCat = productDetails?.parentCategory;
                  const subCat = productDetails?.subCategory;
                  const primaryCategoryId =
                    parentCat != null && typeof parentCat === 'object' && parentCat._id != null
                      ? String(parentCat._id)
                      : typeof parentCat === 'string'
                        ? parentCat
                        : '';
                  const subCategoryId =
                    subCat != null && typeof subCat === 'object' && subCat._id != null
                      ? String(subCat._id)
                      : typeof subCat === 'string'
                        ? subCat
                        : '';
                  return {
                    id: `product-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                    productId: productIdStr,
                    productDetails: productDetails || null,
                    primaryCategoryId: primaryCategoryId || undefined,
                    subCategoryId: subCategoryId || undefined,
                    qty: typeof productData?.qty === 'number' ? productData.qty : 1,
                    unit: unitVal || 'pcs',
                  };
                });
                const sectionDetails = sectionList.find((s: any) => (s._id || s) === sectionIdStr) || (typeof sectionRef === 'object' && sectionRef !== null ? sectionRef : null);
                return {
                  id: `section-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                  sectionId: sectionIdStr,
                  sectionDetails: sectionDetails || null,
                  products: loadedProducts,
                };
              });
              const scheduleDetails = scheduleList.find((s: any) => (s._id || s) === scheduleIdStr) || (typeof scheduleRef === 'object' && scheduleRef !== null ? scheduleRef : null);
              return {
                id: `schedule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                scheduleId: scheduleIdStr,
                scheduleDetails: scheduleDetails || null,
                sections: loadedSections,
              };
            });
            setBOQSchedules(loadedSchedules);
          } else {
            setBOQSchedules([]);
          }
        } catch (error) {
          console.error('Error fetching BOQ:', error);
          toast.error('Failed to load BOQ data');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (!silent) toast.error('Failed to load data');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [isEditMode, id]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Handle customer selection
  const handleCustomerChange = (customerIdValue: string) => {
    setCustomerId(customerIdValue);
    const customer = customers.find((c) => c._id === customerIdValue);
    setSelectedCustomer(customer || null);
    setProjectIndex(-1);
    setSelectedProject(null);
    setProjectName('');
    setProjectAddress('');
  };

  // Handle project selection
  const handleProjectChange = (index: number) => {
    setProjectIndex(index);
    if (selectedCustomer && selectedCustomer.projects && index >= 0) {
      const project = selectedCustomer.projects[index];
      setSelectedProject(project);
      setProjectName(project.name || '');
      setProjectAddress(project.address || '');
    } else {
      setSelectedProject(null);
      setProjectName('');
      setProjectAddress('');
    }
  };

  // Handle load from previous BOQ — normalize all API data to plain objects to avoid render crashes
  const handleLoadFromBOQ = async (boqId: string) => {
    if (!boqId || typeof boqId !== 'string') return;

    try {
      const response = await apiService.getBOQ(boqId);
      const boq = response?.data;
      if (!boq || typeof boq !== 'object') {
        toast.error('Invalid BOQ data received');
        return;
      }

      // Normalize customer to plain object (avoid Mongoose/getters/circular refs)
      const rawCustomer = boq.customer;
      const customerIdVal =
        rawCustomer != null && typeof rawCustomer === 'object' && rawCustomer._id != null
          ? String(rawCustomer._id)
          : typeof rawCustomer === 'string'
            ? rawCustomer
            : '';
      setCustomerId(customerIdVal);
      if (customerIdVal && rawCustomer != null && typeof rawCustomer === 'object') {
        const projects = Array.isArray(rawCustomer.projects)
          ? rawCustomer.projects.map((p: any) => ({
              name: p?.name != null ? String(p.name) : '',
              address: p?.address != null ? String(p.address) : '',
            }))
          : [];
        setSelectedCustomer({
          _id: String(rawCustomer._id),
          name: rawCustomer.name != null ? String(rawCustomer.name) : '',
          company: rawCustomer.company != null ? String(rawCustomer.company) : '',
          email: rawCustomer.email != null ? String(rawCustomer.email) : '',
          phone: rawCustomer.phone != null ? String(rawCustomer.phone) : '',
          projects,
        });
      } else {
        setSelectedCustomer(null);
      }

      setInquiryEmployeeId(
        boq.inquiryOf?._id != null ? String(boq.inquiryOf._id) : typeof boq.inquiryOf === 'string' ? boq.inquiryOf : ''
      );

      setProjectName(boq.projectName != null ? String(boq.projectName) : '');
      setProjectAddress(boq.projectAddress != null ? String(boq.projectAddress) : '');
      setSelectedProject(
        boq.projectName || boq.projectAddress
          ? {
              name: boq.projectName != null ? String(boq.projectName) : '',
              address: boq.projectAddress != null ? String(boq.projectAddress) : '',
            }
          : null
      );

      if (boq.boqDate) {
        try {
          setBoqDate(new Date(boq.boqDate).toISOString().split('T')[0]);
        } catch {
          // keep current
        }
      }

      const rawSchedules = Array.isArray(boq.schedules) ? boq.schedules : [];
      const scheduleList = Array.isArray(schedules) ? schedules : [];
      const sectionList = Array.isArray(sections) ? sections : [];
      const productList = Array.isArray(products) ? products : [];

      const toId = (ref: any) => {
        if (ref == null) return '';
        if (typeof ref === 'string') return ref;
        if (typeof ref === 'object' && ref._id != null) return String(ref._id);
        return '';
      };

      const toPlainSchedule = (s: any) =>
        s && typeof s === 'object'
          ? { _id: s._id != null ? String(s._id) : '', name: s.name != null ? String(s.name) : '', description: s.description != null ? String(s.description) : '' }
          : null;
      const toPlainSection = (s: any) =>
        s && typeof s === 'object'
          ? { _id: s._id != null ? String(s._id) : '', name: s.name != null ? String(s.name) : '', description: s.description != null ? String(s.description) : '' }
          : null;
      const toPlainProduct = (p: any) => {
        if (!p || typeof p !== 'object') return null;
        const brand = p.brand;
        const brandName =
          brand != null && typeof brand === 'object' && brand.name != null ? String(brand.name) : brand != null ? String(brand) : '';
        return {
          _id: p._id != null ? String(p._id) : '',
          name: p.name != null ? String(p.name) : '',
          description: p.description != null ? String(p.description) : '',
          brand: brandName,
          model: p.model != null ? String(p.model) : '',
          specifications: p.specifications != null ? String(p.specifications) : '',
        };
      };

      if (rawSchedules.length > 0) {
        const loadedSchedules = rawSchedules.map((scheduleData: any) => {
          const scheduleIdStr = toId(scheduleData?.schedule);
          const rawSections = Array.isArray(scheduleData?.sections) ? scheduleData.sections : [];
          const loadedSections = rawSections.map((sectionData: any) => {
            const sectionIdStr = toId(sectionData?.section);
            const rawProducts = Array.isArray(sectionData?.products) ? sectionData.products : [];
            const loadedProducts = rawProducts.map((productData: any) => {
              const productIdStr = toId(productData?.product);
              const found = productList.find((p: any) => String(p._id || p) === productIdStr);
              const productDetails = found ? toPlainProduct(found) : null;
              let unitVal = productData?.unit != null ? String(productData.unit) : '';
              if (!unitVal && found?.unit) {
                const u = found.unit;
                if (u != null && typeof u === 'object') {
                  unitVal = (u as any).symbol != null ? String((u as any).symbol) : (u as any).name != null ? String((u as any).name) : '';
                } else if (u != null) {
                  unitVal = String(u);
                }
              }
              const parentCat = found?.parentCategory;
              const subCat = found?.subCategory;
              const primaryCategoryId =
                parentCat != null && typeof parentCat === 'object' && parentCat._id != null
                  ? String(parentCat._id)
                  : typeof parentCat === 'string'
                    ? parentCat
                    : '';
              const subCategoryId =
                subCat != null && typeof subCat === 'object' && subCat._id != null
                  ? String(subCat._id)
                  : typeof subCat === 'string'
                    ? subCat
                    : '';
              return {
                id: `product-${Date.now()}-${Math.random().toString(36).slice(2)}`,
                productId: productIdStr,
                productDetails,
                primaryCategoryId: primaryCategoryId || undefined,
                subCategoryId: subCategoryId || undefined,
                qty: typeof productData?.qty === 'number' && Number.isFinite(productData.qty) ? productData.qty : 1,
                unit: unitVal || 'pcs',
              };
            });
            const sectionDetails = sectionList.find((s: any) => String(s._id || s) === sectionIdStr);
            return {
              id: `section-${Date.now()}-${Math.random().toString(36).slice(2)}`,
              sectionId: sectionIdStr,
              sectionDetails: toPlainSection(sectionDetails) || null,
              products: loadedProducts,
            };
          });
          const scheduleDetails = scheduleList.find((s: any) => String(s._id || s) === scheduleIdStr);
          return {
            id: `schedule-${Date.now()}-${Math.random().toString(36).slice(2)}`,
            scheduleId: scheduleIdStr,
            scheduleDetails: toPlainSchedule(scheduleDetails) || null,
            sections: loadedSections,
          };
        });
        setBOQSchedules(loadedSchedules);
      } else {
        setBOQSchedules([]);
      }

      toast.success('Loaded data from previous BOQ');
    } catch (error) {
      console.error('Error loading BOQ:', error);
      toast.error('Failed to load BOQ');
    }
  };

  const addSchedule = () => {
    const newSchedule: ScheduleData = {
      id: `schedule-${Date.now()}`,
      scheduleId: '',
      scheduleDetails: null,
      sections: [],
    };
    setBOQSchedules([...boqSchedules, newSchedule]);
  };

  const removeSchedule = (scheduleIdx: number) => {
    setBOQSchedules(prev => (Array.isArray(prev) ? prev : []).filter((_, idx) => idx !== scheduleIdx));
  };

  const updateSchedule = (scheduleIdx: number, scheduleId: string) => {
    const schedule = (schedules || []).find((s: any) => (s._id || s) === scheduleId);
    setBOQSchedules(prev => {
      if (scheduleIdx < 0 || scheduleIdx >= prev.length) return prev;
      return prev.map((s, idx) =>
        idx !== scheduleIdx ? s : { ...s, scheduleId, scheduleDetails: schedule || null }
      );
    });
  };

  const addSection = (scheduleIdx: number) => {
    const newSection: SectionData = {
      id: `section-${Date.now()}`,
      sectionId: '',
      sectionDetails: null,
      products: [],
    };
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sections = Array.isArray(schedule.sections) ? schedule.sections : [];
      return prev.map((s, idx) =>
        idx !== scheduleIdx
          ? s
          : { ...s, sections: [...sections, newSection] }
      );
    });
  };

  const removeSection = (scheduleIdx: number, sectionIdx: number) => {
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sections = Array.isArray(schedule.sections) ? schedule.sections : [];
      if (sectionIdx < 0 || sectionIdx >= sections.length) return prev;
      return prev.map((s, idx) =>
        idx !== scheduleIdx
          ? s
          : { ...s, sections: sections.filter((_, i) => i !== sectionIdx) }
      );
    });
  };

  // Sections that belong to the given schedule (Section master assigns sections to schedules)
  const getSectionsForSchedule = (scheduleId: string) =>
    !scheduleId ? [] : (sections || []).filter((sec: any) => (sec.schedules || []).some((s: any) => (s._id || s) === scheduleId));

  const getRefName = (ref: any): string => {
    if (ref == null) return '';
    if (typeof ref === 'object' && ref !== null && typeof ref.name === 'string') return ref.name;
    if (typeof ref === 'string') return ref;
    return String(ref);
  };

  /** Get unit options for a product: if product has one unit → single display value; if multiple → list of { value, label }. */
  const getProductUnitOptions = (product: any, unitsList: any[]): { singleUnit: string | null; options: { value: string; label: string }[] } => {
    if (!product || !unitsList?.length) return { singleUnit: null, options: unitsList?.map((u) => ({ value: u.symbol || u.name || '', label: u.symbol ? `${u.name} (${u.symbol})` : u.name || '' })) || [] };
    const arr = Array.isArray(product.units) ? product.units : [];
    if (arr.length > 0) {
      const options = arr.map((u: any) => {
        const id = u?._id || u;
        const uObj = unitsList.find((ul) => String(ul._id) === String(id));
        const value = uObj ? (uObj.symbol || uObj.name || '') : (typeof u === 'object' && u != null ? u.symbol || u.name || '' : String(u));
        const label = uObj ? (uObj.symbol ? `${uObj.name} (${uObj.symbol})` : uObj.name || '') : value;
        return { value, label };
      }).filter((o: { value: string; label: string }) => o.value);
      if (options.length === 1) return { singleUnit: options[0].value, options: [] };
      if (options.length > 1) return { singleUnit: null, options };
    }
    const single = product.unit;
    if (!single) return { singleUnit: null, options: unitsList.map((u) => ({ value: u.symbol || u.name || '', label: u.symbol ? `${u.name} (${u.symbol})` : u.name || '' })) };
    const unitId = typeof single === 'object' && single != null ? single._id : single;
    const uObj = unitsList.find((ul) => String(ul._id) === String(unitId));
    const display = uObj ? (uObj.symbol || uObj.name || '') : (typeof single === 'object' && single != null ? single.symbol || single.name || '' : String(single));
    return { singleUnit: display, options: [] };
  };

  // Catalog items assigned to the given section (section/sections on catalog page)
  const getProductsForSection = (sectionId: string) => {
    if (!sectionId) return [];
    return (products || []).filter((p: any) => {
      const singleId = p.section != null && (typeof p.section === 'object' ? p.section._id : p.section);
      const inSingle = singleId === sectionId;
      const inMulti = (p.sections || []).some((s: any) => (s._id || s) === sectionId);
      return inSingle || inMulti;
    });
  };

  // Primary categories (no parent) from categories page
  const primaryCategories = React.useMemo(
    () =>
      (categories || []).filter((c) => {
        const p = c.parent;
        return p == null || p === '' || (typeof p === 'string' && !p);
      }),
    [categories]
  );

  // Sub categories under a primary (parent._id === primaryId)
  const getSubCategoriesForPrimary = (primaryId: string) =>
    (categories || []).filter((c) => {
      const p = c.parent;
      if (!p) return false;
      const parentId = typeof p === 'object' && p !== null ? p._id : p;
      return parentId === primaryId;
    });

  // Products filtered by primary category and/or sub category (within optional pool, default section products)
  const getFilteredProducts = (primaryCategoryId?: string, subCategoryId?: string, productPool?: any[]) => {
    const pool = productPool ?? products ?? [];
    if (!primaryCategoryId && !subCategoryId) return pool;
    return pool.filter((p: any) => {
      const pParentId = p.parentCategory && typeof p.parentCategory === 'object' ? p.parentCategory._id : (p.parentCategory as string) || '';
      const pSubId = p.subCategory && typeof p.subCategory === 'object' ? p.subCategory._id : (p.subCategory as string) || '';
      if (subCategoryId) return pSubId === subCategoryId;
      if (primaryCategoryId) return pParentId === primaryCategoryId;
      return true;
    });
  };

  const handlePrimaryCategoryChange = (scheduleIdx: number, sectionIdx: number, productIdx: number, primaryCategoryId: string) => {
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sectionsList = Array.isArray(schedule.sections) ? schedule.sections : [];
      const section = sectionsList[sectionIdx];
      if (!section) return prev;
      const productsList = Array.isArray(section.products) ? section.products : [];
      const row = productsList[productIdx];
      if (!row) return prev;
      const productsInSection = getProductsForSection(section.sectionId);
      const filtered = getFilteredProducts(primaryCategoryId, '', productsInSection);
      const keepProduct = row.productId && filtered.some((p: any) => (p._id || p) === row.productId);
      return prev.map((s, sIdx) => {
        if (sIdx !== scheduleIdx) return s;
        const secs = Array.isArray(s.sections) ? s.sections : [];
        return {
          ...s,
          sections: secs.map((sec, secIdx) => {
            if (secIdx !== sectionIdx) return sec;
            const prods = Array.isArray(sec.products) ? sec.products : [];
            return {
              ...sec,
              products: prods.map((p, pIdx) =>
                pIdx !== productIdx
                  ? p
                  : {
                      ...p,
                      primaryCategoryId,
                      subCategoryId: '',
                      ...(keepProduct ? {} : { productId: '', productDetails: null, unit: '' }),
                    }
              ),
            };
          }),
        };
      });
    });
    setProductSearchOpen(null);
    setProductSearchQuery('');
  };

  const handleSubCategoryChange = (scheduleIdx: number, sectionIdx: number, productIdx: number, subCategoryId: string) => {
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sectionsList = Array.isArray(schedule.sections) ? schedule.sections : [];
      const section = sectionsList[sectionIdx];
      if (!section) return prev;
      const productsList = Array.isArray(section.products) ? section.products : [];
      const row = productsList[productIdx];
      if (!row) return prev;
      const productsInSection = getProductsForSection(section.sectionId);
      const filtered = getFilteredProducts(row.primaryCategoryId || '', subCategoryId, productsInSection);
      const keepProduct = row.productId && filtered.some((p: any) => (p._id || p) === row.productId);
      return prev.map((s, sIdx) => {
        if (sIdx !== scheduleIdx) return s;
        const secs = Array.isArray(s.sections) ? s.sections : [];
        return {
          ...s,
          sections: secs.map((sec, secIdx) => {
            if (secIdx !== sectionIdx) return sec;
            const prods = Array.isArray(sec.products) ? sec.products : [];
            return {
              ...sec,
              products: prods.map((p, pIdx) =>
                pIdx !== productIdx
                  ? p
                  : {
                      ...p,
                      subCategoryId,
                      ...(keepProduct ? {} : { productId: '', productDetails: null, unit: '' }),
                    }
              ),
            };
          }),
        };
      });
    });
    setProductSearchOpen(null);
    setProductSearchQuery('');
  };

  const updateSection = (scheduleIdx: number, sectionIdx: number, sectionId: string) => {
    const section = sections.find(s => s._id === sectionId);
    const allowedIds = getProductsForSection(sectionId).map((p: any) => p._id);
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sectionList = Array.isArray(schedule.sections) ? schedule.sections : [];
      const sec = sectionList[sectionIdx];
      if (!sec) return prev;
      const products = Array.isArray(sec.products) ? sec.products : [];
      const newProducts = products.map((row: any) =>
        row.productId && !allowedIds.includes(row.productId)
          ? { ...row, productId: '', productDetails: null }
          : row
      );
      return prev.map((s, idx) => {
        if (idx !== scheduleIdx) return s;
        const secs = Array.isArray(s.sections) ? s.sections : [];
        return {
          ...s,
          sections: secs.map((secItem, si) =>
            si !== sectionIdx
              ? secItem
              : { ...secItem, sectionId, sectionDetails: section || null, products: newProducts }
          ),
        };
      });
    });
  };

  const addProduct = (scheduleIdx: number, sectionIdx: number) => {
    const newProduct: ProductRow = {
      id: `product-${Date.now()}`,
      productId: '',
      productDetails: null,
      primaryCategoryId: '',
      subCategoryId: '',
      qty: 1,
      unit: 'pcs',
    };
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sections = Array.isArray(schedule.sections) ? schedule.sections : [];
      const section = sections[sectionIdx];
      if (!section) return prev;
      return prev.map((s, idx) => {
        if (idx !== scheduleIdx) return s;
        const secs = Array.isArray(s.sections) ? s.sections : [];
        return {
          ...s,
          sections: secs.map((sec, si) =>
            si !== sectionIdx
              ? sec
              : { ...sec, products: [...(Array.isArray(sec.products) ? sec.products : []), newProduct] }
          ),
        };
      });
    });
  };

  const removeProduct = (scheduleIdx: number, sectionIdx: number, productIdx: number) => {
    setBOQSchedules(prev => {
      const schedule = prev[scheduleIdx];
      if (!schedule) return prev;
      const sections = Array.isArray(schedule.sections) ? schedule.sections : [];
      const section = sections[sectionIdx];
      if (!section) return prev;
      const products = Array.isArray(section.products) ? section.products : [];
      if (productIdx < 0 || productIdx >= products.length) return prev;
      return prev.map((s, idx) => {
        if (idx !== scheduleIdx) return s;
        const secs = Array.isArray(s.sections) ? s.sections : [];
        return {
          ...s,
          sections: secs.map((sec, si) =>
            si !== sectionIdx
              ? sec
              : { ...sec, products: products.filter((_, i) => i !== productIdx) }
          ),
        };
      });
    });
  };

  const updateProduct = (scheduleIdx: number, sectionIdx: number, productIdx: number, field: string, value: any) => {
    setBOQSchedules(prevSchedules => {
      const scheduleList = prevSchedules || [];
      const updated = scheduleList.map((schedule, sIdx) => {
        if (sIdx !== scheduleIdx) return schedule;
        const sectionsList = Array.isArray(schedule.sections) ? schedule.sections : [];
        return {
          ...schedule,
          sections: sectionsList.map((section, secIdx) => {
            if (secIdx !== sectionIdx) return section;
            const productsList = Array.isArray(section.products) ? section.products : [];
            return {
              ...section,
              products: productsList.map((product, pIdx) => {
                if (pIdx !== productIdx) return product;
                
                if (field === 'productId') {
                  const selectedProduct = products.find(p => p._id === value);
                  const parentCat = selectedProduct?.parentCategory;
                  const subCat = selectedProduct?.subCategory;
                  const primaryCategoryId =
                    parentCat != null && typeof parentCat === 'object' && parentCat._id != null
                      ? String(parentCat._id)
                      : typeof parentCat === 'string'
                        ? parentCat
                        : '';
                  const subCategoryId =
                    subCat != null && typeof subCat === 'object' && subCat._id != null
                      ? String(subCat._id)
                      : typeof subCat === 'string'
                        ? subCat
                        : '';

                  // Auto-fill unit: one unit → autofill; multiple units → set first, user picks from dropdown
                  let unitValue = '';
                  if (selectedProduct) {
                    const { singleUnit, options } = getProductUnitOptions(selectedProduct, units);
                    if (singleUnit) {
                      unitValue = singleUnit;
                    } else if (options.length > 0) {
                      unitValue = options[0].value;
                    }
                  }
                  
                  return {
                    ...product,
                    productId: value,
                    productDetails: selectedProduct || null,
                    primaryCategoryId: primaryCategoryId || undefined,
                    subCategoryId: subCategoryId || undefined,
                    unit: unitValue,
                  };
                } else {
                  return {
                    ...product,
                    [field]: value,
                  };
                }
              }),
            };
          }),
        };
      });
      
      return updated;
    });
  };

  const handleSave = async () => {
    if (!customerId) {
      toast.error('Please select a customer');
      return;
    }

    if (boqSchedules.length === 0) {
      toast.error('Please add at least one schedule');
      return;
    }

    try {
      setSaving(true);

      const inquiryEmployee = employees.find((e) => e._id === inquiryEmployeeId);
      const boqData = {
        customer: customerId,
        inquiryOf: inquiryEmployeeId || undefined,
        inquiryOfDesignation: inquiryEmployee?.designation || undefined,
        projectName,
        projectAddress,
        boqDate: new Date(boqDate).toISOString(),
        schedules: (Array.isArray(boqSchedules) ? boqSchedules : []).map(schedule => ({
          schedule: schedule.scheduleId || undefined,
          scheduleName: schedule.scheduleDetails?.name || '',
          scheduleDescription: schedule.scheduleDetails?.description || '',
          sections: (Array.isArray(schedule.sections) ? schedule.sections : []).map(section => ({
            section: section.sectionId || undefined,
            sectionName: section.sectionDetails?.name || '',
            sectionDescription: section.sectionDetails?.description || '',
            products: (Array.isArray(section.products) ? section.products : []).map(product => ({
              product: product.productId || undefined,
              name: product.productDetails?.name || '',
              description: product.productDetails?.description || '',
              brand: product.productDetails?.brand || '',
              model: product.productDetails?.model || '',
              specifications: product.productDetails?.specifications || '',
              qty: Number(product.qty) || 1,
              unit: product.unit,
            })),
          })),
        })),
        status: 'draft',
      };

      console.log('Saving BOQ:', boqData);
      let response;
      if (isEditMode && id) {
        response = await apiService.updateBOQ(id, boqData);
        console.log('BOQ updated successfully:', response.data);
        toast.success('BOQ updated successfully');
      } else {
        response = await apiService.createBOQ(boqData);
        console.log('BOQ created successfully:', response.data);
        toast.success('BOQ created successfully');
      }
      navigate('/boqs');
    } catch (error: any) {
      console.error('Error saving BOQ:', error);
      console.error('Error response:', error.response?.data);
      
      // Display detailed error message
      const errorMessage = error.response?.data?.details 
        || error.response?.data?.message 
        || error.response?.data?.errors?.join(', ')
        || 'Failed to save BOQ';
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <CreateBOQErrorBoundary>
    <div className="space-y-3 pb-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white border-b border-gray-200 -mx-6 px-6 -mt-6 py-3 sticky top-0 z-10 shadow-sm"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              {isEditMode ? 'Edit BOQ' : 'Create BOQ'}
            </h1>
            <p className="text-xs text-gray-500" style={{ fontFamily: 'var(--font-family)' }}>
              Build a bill of quantities with schedules, sections, and products
            </p>
          </div>
          <div className="flex space-x-2">
            <Button variant="outline" size="sm" onClick={() => navigate('/boqs')} icon={<X className="w-3 h-3" />}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} loading={saving} icon={<Save className="w-3 h-3" />}>
              Save BOQ
            </Button>
          </div>
        </div>

        {/* BOQ Info Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">BOQ No.</label>
            <input
              type="text"
              value="Will be auto-generated"
              readOnly
              className="form-input px-2 py-1.5 text-sm bg-gray-50 text-gray-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">BOQ Date</label>
            <div className="relative">
              <Calendar className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
              <input
                type="date"
                value={boqDate}
                onChange={(e) => setBoqDate(e.target.value)}
                className="form-input pl-7 px-2 py-1.5 text-sm"
              />
            </div>
          </div>
        </div>
      </motion.div>

      {/* BOQ Options */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
            <ClipboardList className="w-4 h-4 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              BOQ Options
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Load from Previous BOQ */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Load from Previous BOQ
              </label>
              <select
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) handleLoadFromBOQ(v);
                }}
                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontFamily: 'var(--font-family)' }}
                defaultValue=""
              >
                <option value="">Select a BOQ</option>
                {(Array.isArray(boqsList) ? boqsList : []).map((boq: any) => (
                  <option key={boq._id} value={boq._id}>
                    {boq.boqNumber} - {boq.customer?.name || boq.customer?.company || 'N/A'}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Customer Information */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
            <User className="w-4 h-4 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Customer Information
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <div className="flex items-center justify-between gap-2 mb-1">
                <label className="text-xs font-medium text-gray-700">Customer *</label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCreateCustomerModalOpen(true)}
                  icon={<Plus className="w-3 h-3" />}
                >
                  Create New
                </Button>
              </div>
              <select
                value={customerId}
                onChange={(e) => handleCustomerChange(e.target.value)}
                className="form-select px-2 py-1.5 text-sm"
                required
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer._id} value={customer._id}>
                    {customer.name} {customer.company && `(${customer.company})`}
                  </option>
                ))}
              </select>
            </div>

            {selectedCustomer && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Company</label>
                  <input
                    type="text"
                    value={selectedCustomer.company || '-'}
                    readOnly
                    className="form-input px-2 py-1.5 text-sm bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="text"
                    value={selectedCustomer.email || '-'}
                    readOnly
                    className="form-input px-2 py-1.5 text-sm bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="text"
                    value={selectedCustomer.phone || '-'}
                    readOnly
                    className="form-input px-2 py-1.5 text-sm bg-gray-50"
                  />
                </div>
              </>
            )}
          </div>

          {/* Project Selection */}
          {selectedCustomer && selectedCustomer.projects && selectedCustomer.projects.length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Name</label>
                <select
                  value={projectIndex}
                  onChange={(e) => handleProjectChange(parseInt(e.target.value))}
                  className="form-select px-2 py-1.5 text-sm"
                >
                  <option value={-1}>Select Project</option>
                  {selectedCustomer.projects.map((project: any, idx: number) => (
                    <option key={idx} value={idx}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {selectedProject && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Project Address</label>
                  <input
                    type="text"
                    value={selectedProject.address || '-'}
                    readOnly
                    className="form-input px-2 py-1.5 text-sm bg-gray-50"
                  />
                </div>
              )}
            </div>
          )}

          {/* Manual Project Entry (if no customer selected or no projects available) */}
          {(!selectedCustomer || !selectedCustomer.projects || selectedCustomer.projects.length === 0) && (
            <div className="mt-3 pt-3 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Name</label>
                <input
                  type="text"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="Enter project name"
                  className="form-input px-2 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Project Address</label>
                <input
                  type="text"
                  value={projectAddress}
                  onChange={(e) => setProjectAddress(e.target.value)}
                  placeholder="Enter project address"
                  className="form-input px-2 py-1.5 text-sm"
                />
              </div>
            </div>
          )}
        </Card>
      </motion.div>

      {/* Inquiry of */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
        <Card padding="sm" className="overflow-hidden">
          <div className="flex items-center mb-2 pb-2 border-b border-gray-200">
            <UserCircle className="w-4 h-4 text-blue-600 mr-2" />
            <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
              Inquiry of
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Employee Name</label>
              <select
                value={inquiryEmployeeId}
                onChange={(e) => setInquiryEmployeeId(e.target.value)}
                className="form-select px-2 py-1.5 text-sm"
              >
                <option value="">Select Employee</option>
                {employees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Designation</label>
              <input
                type="text"
                value={employees.find((e) => e._id === inquiryEmployeeId)?.designation || ''}
                readOnly
                placeholder="Auto-filled from employee"
                className="form-input px-2 py-1.5 text-sm bg-gray-50"
              />
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Schedules */}
      {(Array.isArray(boqSchedules) ? boqSchedules : []).map((schedule, scheduleIdx) => (
        <motion.div
          key={schedule.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 + scheduleIdx * 0.1 }}
        >
          <Card padding="sm" className="border border-gray-300 overflow-hidden">
            <div className="flex items-center justify-between mb-2 pb-2 border-b border-gray-200">
              <div className="flex items-center">
                <ClipboardList className="w-4 h-4 text-blue-600 mr-2" />
                <h2 className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'var(--font-family)' }}>
                  Schedule {scheduleIdx + 1}
                </h2>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeSchedule(scheduleIdx)}
                icon={<Trash2 className="w-3.5 h-3.5 text-red-600" />}
              >
                Remove Schedule
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-2">
              <div className="md:col-span-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <label className="text-xs font-medium text-gray-700">Schedule</label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setCreateScheduleModalOpen(true)}
                    icon={<Plus className="w-3 h-3" />}
                  >
                    Create New
                  </Button>
                </div>
                <select
                  value={schedule.scheduleId}
                  onChange={(e) => updateSchedule(scheduleIdx, e.target.value)}
                  className="form-select px-2 py-1.5 text-sm"
                >
                  <option value="">Select Schedule</option>
                  {Array.isArray(schedules) && schedules.map((s) => (
                    <option key={s._id} value={s._id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              {schedule.scheduleDetails?.description && (
                <div className="md:col-span-3">
                  <p className="text-xs text-gray-500 italic break-words overflow-wrap-anywhere">{schedule.scheduleDetails.description}</p>
                </div>
              )}
            </div>

            {/* Sections */}
            {(Array.isArray(schedule.sections) ? schedule.sections : []).map((section, sectionIdx) => (
              <div key={section.id} className="mt-3 p-2 bg-gray-50 border border-gray-300 rounded">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-semibold text-gray-900">Section {sectionIdx + 1}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeSection(scheduleIdx, sectionIdx)}
                    icon={<Trash2 className="w-3 h-3 text-red-600" />}
                  >
                    Remove
                  </Button>
                </div>

                <div className="grid grid-cols-1 gap-2">
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <label className="text-xs font-medium text-gray-700">Section</label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setCreateSectionModalOpen(true)}
                        icon={<Plus className="w-3 h-3" />}
                      >
                        Create New
                      </Button>
                    </div>
                    <select
                      value={section.sectionId}
                      onChange={(e) => updateSection(scheduleIdx, sectionIdx, e.target.value)}
                      className="form-select px-2 py-1.5 text-sm"
                    >
                      <option value="">Select Section</option>
                      {getSectionsForSchedule(schedule.scheduleId).map((s: any) => (
                        <option key={s._id} value={s._id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {section.sectionDetails?.description && (
                    <div>
                      <p className="text-xs text-gray-500 italic break-words overflow-wrap-anywhere">{section.sectionDetails.description}</p>
                    </div>
                  )}
                </div>

                {/* Products */}
                {(Array.isArray(section.products) ? section.products : []).map((product, productIdx) => (
                  <div key={product.id} className="mt-2 p-2 bg-white border border-gray-200 rounded">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs font-semibold text-gray-900">Product {productIdx + 1}</h4>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeProduct(scheduleIdx, sectionIdx, productIdx)}
                        icon={<Trash2 className="w-3 h-3 text-red-600" />}
                      >
                        Remove
                      </Button>
                    </div>

                    <div className="space-y-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Primary Category</label>
                        <select
                          value={product.primaryCategoryId || ''}
                          onChange={(e) => handlePrimaryCategoryChange(scheduleIdx, sectionIdx, productIdx, e.target.value)}
                          className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                        >
                          <option value="">All categories</option>
                          {primaryCategories.map((c) => (
                            <option key={c._id} value={c._id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      {product.primaryCategoryId && getSubCategoriesForPrimary(product.primaryCategoryId).length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Sub Category</label>
                          <select
                            value={product.subCategoryId || ''}
                            onChange={(e) => handleSubCategoryChange(scheduleIdx, sectionIdx, productIdx, e.target.value)}
                            className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white"
                          >
                            <option value="">All (primary)</option>
                            {getSubCategoriesForPrimary(product.primaryCategoryId).map((c) => (
                              <option key={c._id} value={c._id}>
                                {c.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="relative">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <label className="text-xs font-medium text-gray-700">Product *</label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setCreateProductModalOpen(true)}
                            icon={<Plus className="w-3 h-3" />}
                          >
                            Create New
                          </Button>
                        </div>
                        {(() => {
                          const isOpen =
                            productSearchOpen?.scheduleIdx === scheduleIdx &&
                            productSearchOpen?.sectionIdx === sectionIdx &&
                            productSearchOpen?.productIdx === productIdx;
                          const productsInSection = getProductsForSection(section.sectionId);
                          const filteredByCat = getFilteredProducts(product.primaryCategoryId, product.subCategoryId, productsInSection);
                          const searchLower = (productSearchQuery || '').trim().toLowerCase();
                          const filteredProducts = searchLower
                            ? filteredByCat.filter(
                                (p: any) =>
                                  (p.name || '').toLowerCase().includes(searchLower) ||
                                  (p.model || '').toLowerCase().includes(searchLower)
                              )
                            : filteredByCat;
                          const displayLabel = product.productDetails
                            ? `${product.productDetails.name || ''}${product.productDetails.model ? ` - ${product.productDetails.model}` : ''}`
                            : 'Select Product';
                          const listboxId = `product-listbox-boq-${scheduleIdx}-${sectionIdx}-${productIdx}`;
                          return (
                            <>
                              <div
                                role="combobox"
                                aria-expanded={isOpen}
                                aria-haspopup="listbox"
                                aria-controls={listboxId}
                                onClick={() => {
                                  setProductSearchOpen({ scheduleIdx, sectionIdx, productIdx });
                                  setProductSearchQuery('');
                                }}
                                className="block w-full px-2 py-1.5 text-sm border border-gray-300 rounded shadow-sm focus:outline-none focus:ring-1 focus:ring-orange-500 focus:border-orange-500 bg-white cursor-pointer text-left flex items-center justify-between"
                              >
                                <span className={product.productId ? 'text-gray-900' : 'text-gray-500'}>
                                  {isOpen && productSearchQuery !== undefined ? (productSearchQuery || 'Type to search...') : displayLabel}
                                </span>
                                <span className="text-gray-400">▼</span>
                              </div>
                              {isOpen && (
                                <>
                                  <div
                                    className="absolute z-20 mt-0.5 w-full bg-white border border-gray-300 rounded shadow-lg max-h-56 overflow-hidden"
                                    style={{ minWidth: '100%' }}
                                  >
                                    <input
                                      type="text"
                                      autoFocus
                                      value={productSearchQuery}
                                      onChange={(e) => setProductSearchQuery(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Escape') {
                                          setProductSearchOpen(null);
                                          setProductSearchQuery('');
                                        }
                                      }}
                                      placeholder="Type to search..."
                                      className="w-full px-2 py-1.5 text-sm border-b border-gray-200 focus:outline-none focus:ring-0"
                                    />
                                    <ul
                                      id={listboxId}
                                      role="listbox"
                                      className="max-h-48 overflow-y-auto"
                                    >
                                      {filteredProducts.length === 0 ? (
                                        <li className="px-2 py-2 text-xs text-gray-500">No products match</li>
                                      ) : (
                                        filteredProducts.map((prod: any) => (
                                          <li
                                            key={prod._id}
                                            role="option"
                                            aria-selected={prod._id === product.productId}
                                            onClick={() => {
                                              updateProduct(scheduleIdx, sectionIdx, productIdx, 'productId', prod._id);
                                              setProductSearchOpen(null);
                                              setProductSearchQuery('');
                                            }}
                                            className={`px-2 py-1.5 text-sm cursor-pointer hover:bg-orange-50 ${prod._id === product.productId ? 'bg-orange-100' : ''}`}
                                          >
                                            {prod.name} {prod.model ? `- ${prod.model}` : ''}
                                          </li>
                                        ))
                                      )}
                                    </ul>
                                  </div>
                                  <div
                                    className="fixed inset-0 z-10"
                                    aria-hidden="true"
                                    onClick={() => {
                                      setProductSearchOpen(null);
                                      setProductSearchQuery('');
                                    }}
                                  />
                                </>
                              )}
                            </>
                          );
                        })()}
                      </div>

                      {product.productDetails && (
                        <div className="p-2 bg-gray-50 rounded text-xs border border-gray-200 space-y-1">
                          {product.productDetails.brand != null && (product.productDetails.brand !== '' || (typeof product.productDetails.brand === 'object' && product.productDetails.brand !== null)) && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Brand:</strong> {getRefName(product.productDetails.brand)}</p>
                          )}
                          {product.productDetails.model != null && product.productDetails.model !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Model:</strong> {String(product.productDetails.model)}</p>
                          )}
                          {product.productDetails.type != null && (product.productDetails.type !== '' || (typeof product.productDetails.type === 'object' && product.productDetails.type !== null)) && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Type:</strong> {getRefName(product.productDetails.type)}</p>
                          )}
                          {product.productDetails.compressorType != null && (product.productDetails.compressorType !== '' || (typeof product.productDetails.compressorType === 'object' && product.productDetails.compressorType !== null)) && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Compressor Type:</strong> {getRefName(product.productDetails.compressorType)}</p>
                          )}
                          {product.productDetails.countryOfOrigin != null && (product.productDetails.countryOfOrigin !== '' || (typeof product.productDetails.countryOfOrigin === 'object' && product.productDetails.countryOfOrigin !== null)) && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Country of Origin:</strong> {getRefName(product.productDetails.countryOfOrigin)}</p>
                          )}
                          {product.productDetails.countryOfAssembly != null && (product.productDetails.countryOfAssembly !== '' || (typeof product.productDetails.countryOfAssembly === 'object' && product.productDetails.countryOfAssembly !== null)) && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Country of Assembly:</strong> {getRefName(product.productDetails.countryOfAssembly)}</p>
                          )}
                          {product.productDetails.details != null && product.productDetails.details !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Details:</strong> {String(product.productDetails.details)}</p>
                          )}
                          {product.productDetails.description != null && product.productDetails.description !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Description:</strong> {String(product.productDetails.description)}</p>
                          )}
                          {product.productDetails.specifications != null && product.productDetails.specifications !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Specifications:</strong> {String(product.productDetails.specifications)}</p>
                          )}
                          {product.productDetails.coolingCapacity != null && product.productDetails.coolingCapacity !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Cooling Capacity:</strong> {String(product.productDetails.coolingCapacity)}</p>
                          )}
                          {product.productDetails.serialNo != null && product.productDetails.serialNo !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Serial No:</strong> {String(product.productDetails.serialNo)}</p>
                          )}
                          {product.productDetails.partNo != null && product.productDetails.partNo !== '' && (
                            <p className="break-words overflow-wrap-anywhere"><strong>Part No:</strong> {String(product.productDetails.partNo)}</p>
                          )}
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            value={product.qty === '' ? '' : product.qty}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === '') {
                                updateProduct(scheduleIdx, sectionIdx, productIdx, 'qty', '');
                                return;
                              }
                              const n = parseFloat(raw);
                              updateProduct(scheduleIdx, sectionIdx, productIdx, 'qty', Number.isFinite(n) && n >= 1 ? n : 1);
                            }}
                            className="form-input px-1.5 py-1 text-xs"
                          />
                        </div>

                        <div>
                          <label className="block text-[10px] font-medium text-gray-700 mb-0.5">Unit</label>
                          {(() => {
                            const { singleUnit, options } = getProductUnitOptions(product.productDetails, units);
                            if (singleUnit != null) {
                              return (
                                <input
                                  type="text"
                                  readOnly
                                  value={product.unit || singleUnit}
                                  className="form-input px-1.5 py-1 text-xs bg-gray-100 border border-gray-200 rounded"
                                />
                              );
                            }
                            const unitOptions = options.length > 0 ? options : (Array.isArray(units) ? units.map((u) => ({ value: u.symbol || u.name || '', label: u.symbol ? `${u.name} (${u.symbol})` : u.name || '' })) : []);
                            return (
                              <select
                                key={`unit-${product.id}-${product.productId}-${product.unit || 'none'}`}
                                value={product.unit || ''}
                                onChange={(e) => updateProduct(scheduleIdx, sectionIdx, productIdx, 'unit', e.target.value)}
                                className="form-select px-1.5 py-1 text-xs"
                              >
                                <option value="">Select Unit</option>
                                {unitOptions.map((opt, idx) => (
                                  <option key={opt.value + idx} value={opt.value}>
                                    {opt.label || opt.value}
                                  </option>
                                ))}
                              </select>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addProduct(scheduleIdx, sectionIdx)}
                  icon={<Plus className="w-3 h-3" />}
                  className="mt-2 w-full"
                >
                  Add Product
                </Button>
              </div>
            ))}

            <Button
              variant="outline"
              size="sm"
              onClick={() => addSection(scheduleIdx)}
              icon={<Plus className="w-3 h-3" />}
              className="mt-3 w-full"
            >
              Add Section
            </Button>
          </Card>
        </motion.div>
      ))}

      {/* Add Schedule Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Button
          variant="outline"
          onClick={addSchedule}
          icon={<Plus className="w-3 h-3" />}
          className="w-full md:w-auto"
          size="sm"
        >
          Add Schedule
        </Button>
      </motion.div>

      <CreateCustomerModal
        isOpen={createCustomerModalOpen}
        onClose={() => setCreateCustomerModalOpen(false)}
        onSuccess={(customer) => {
          fetchAllData({ silent: true });
          if (customer?._id) {
            setCustomerId(customer._id);
            setSelectedCustomer(customer);
          }
        }}
      />
      <CreateScheduleModal
        isOpen={createScheduleModalOpen}
        onClose={() => setCreateScheduleModalOpen(false)}
        onSuccess={() => {
          fetchAllData({ silent: true });
        }}
      />
      <CreateSectionModal
        isOpen={createSectionModalOpen}
        onClose={() => setCreateSectionModalOpen(false)}
        onSuccess={() => {
          fetchAllData({ silent: true });
        }}
      />
      <CreateProductModal
        isOpen={createProductModalOpen}
        onClose={() => setCreateProductModalOpen(false)}
        onSuccess={() => {
          fetchAllData({ silent: true });
        }}
      />
    </div>
    </CreateBOQErrorBoundary>
  );
};

export default CreateBOQPage;

